import { useEffect } from 'react';

const DIALOG_SELECTOR = '.legacy-modal-overlay[role="dialog"], .notification-center[role="dialog"]';
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusables(dialog: HTMLElement): HTMLElement[] {
  return [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((element) => element.offsetParent !== null || element === document.activeElement);
}

function closeControl(dialog: HTMLElement): HTMLElement | null {
  const byClass = dialog.querySelector<HTMLElement>('.modal-close, button[aria-label^="Fechar"], button[aria-label^="fechar"]');
  if (byClass) return byClass;
  return [...dialog.querySelectorAll<HTMLElement>('button')].find((button) => /^(cancelar|fechar|voltar)$/iu.test(button.textContent?.trim() ?? '')) ?? null;
}

function initialFocus(dialog: HTMLElement): HTMLElement | null {
  const preferred = dialog.querySelector<HTMLElement>('[autofocus], input:not([type="checkbox"]):not([type="hidden"]), textarea, select');
  if (preferred && !preferred.hasAttribute('disabled')) return preferred;
  const candidates = focusables(dialog).filter((element) => !element.classList.contains('modal-close'));
  return candidates[0] ?? focusables(dialog)[0] ?? null;
}

/**
 * Acessibilidade dos modais sem reescrever cada um: observa o DOM e, para todo
 * `role="dialog"` do app, foca o primeiro campo ao abrir, prende o Tab dentro do diálogo,
 * fecha com Escape pelo botão de cancelar/fechar e devolve o foco ao elemento de origem.
 */
export function DialogA11y(): null {
  useEffect(() => {
    const stack: { dialog: HTMLElement; opener: HTMLElement | null }[] = [];
    const top = (): HTMLElement | null => stack.at(-1)?.dialog ?? null;

    const register = (dialog: HTMLElement): void => {
      if (stack.some((entry) => entry.dialog === dialog)) return;
      stack.push({ dialog, opener: document.activeElement instanceof HTMLElement ? document.activeElement : null });
      window.requestAnimationFrame(() => {
        if (!dialog.isConnected) return;
        if (dialog.contains(document.activeElement)) return;
        initialFocus(dialog)?.focus();
      });
    };
    const unregister = (dialog: HTMLElement): void => {
      const index = stack.findIndex((entry) => entry.dialog === dialog);
      if (index === -1) return;
      const [entry] = stack.splice(index, 1);
      const opener = entry?.opener;
      if (opener?.isConnected && !top()) window.requestAnimationFrame(() => opener.focus());
    };

    document.querySelectorAll<HTMLElement>(DIALOG_SELECTOR).forEach(register);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches(DIALOG_SELECTOR)) register(node);
          node.querySelectorAll<HTMLElement>(DIALOG_SELECTOR).forEach(register);
        });
        mutation.removedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches(DIALOG_SELECTOR)) unregister(node);
          node.querySelectorAll<HTMLElement>(DIALOG_SELECTOR).forEach(unregister);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    const onKeyDown = (event: KeyboardEvent): void => {
      const dialog = top();
      if (!dialog) return;
      if (event.key === 'Escape') {
        const control = closeControl(dialog);
        if (!control) return;
        event.preventDefault();
        event.stopPropagation();
        control.click();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables(dialog);
      if (!items.length) { event.preventDefault(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      if (!dialog.contains(active)) { event.preventDefault(); first.focus(); return; }
      if (event.shiftKey && active === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && active === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      observer.disconnect();
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, []);

  return null;
}
